import React, { useState, useCallback } from 'react';
import {
  Loader2,
  ShieldCheck,
  MessagesSquare,
  Send,
  Megaphone,
  Check,
  X,
  Link2,
  Blocks,
  AlertTriangle,
  Globe,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { appConfig } from '@common/config/app.config';
import { BLUETICKS_TOKEN_KEY } from '@common/reusable/common';
import {
  accessChoicesFor,
  parseRedirectHost,
  selectVisibleCapabilities,
  type McpAccessLevel,
} from './mcp-consent-safety';
import { MCPAccessLevelPicker } from './mcp-access-level-picker';

interface MCPOAuthPageProps {
  client_id?: string;
  redirect_uri?: string;
  state?: string;
  scope?: string;
}

const CAPABILITIES = [
  {
    Icon: MessagesSquare,
    title: 'Read your chats & messages',
    detail: 'View WhatsApp conversations, contacts, and history to answer on your behalf.',
    write: false,
  },
  {
    Icon: Send,
    title: 'Send & schedule messages',
    detail: 'Compose, send, and schedule WhatsApp messages from your account.',
    write: true,
  },
  {
    Icon: Megaphone,
    title: 'Manage campaigns & audiences',
    detail: 'Create and run bulk campaigns to your audiences.',
    write: true,
  },
];

/** A vertically-staggered fade-in, so the card assembles on load. */
const reveal = (i: number): React.CSSProperties => ({
  animationDelay: `${i * 70}ms`,
});

export const MCPOAuthPage: React.FC<MCPOAuthPageProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const client_id = searchParams.get('client_id') || '';
  const redirect_uri = searchParams.get('redirect_uri') || '';
  const state = searchParams.get('state') || '';
  const scope = searchParams.get('scope') || '';
  const internal_state = searchParams.get('internal_state') || '';

  // BE#282: fail CLOSED — an empty/absent scope is read-only, not full write.
  // #755: the user may narrow the grant to read-only; full is offered only when requested.
  const { fullAllowed, preselected } = accessChoicesFor(scope);
  const [access, setAccess] = useState<McpAccessLevel>(preselected);
  const canWrite = fullAllowed && access === 'full';
  // BE#282: the redirect_uri host is the only field that reveals where the
  // user's access is actually sent (an attacker's callback).
  const { host: redirectHost, valid: redirectValid } = parseRedirectHost(redirect_uri);
  // BE#282: a read-only grant must not show write capability cards under a
  // "Read-only access" badge — the disclosed capabilities must match the grant.
  const visibleCapabilities = selectVisibleCapabilities(CAPABILITIES, canWrite);

  const handleConsent = useCallback(
    async (approved: boolean) => {
      setIsLoading(true);
      setError(null);

      try {
        const jwtToken = localStorage.getItem(BLUETICKS_TOKEN_KEY);
        if (!jwtToken) {
          setError('No authentication token found. Please log in again.');
          setIsLoading(false);
          return;
        }

        const backendUrl = appConfig.API_ENDPOINT.replace(/\/$/, '');
        const resp = await fetch(`${backendUrl}/mcp/auth/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            internal_state,
            state,
            jwt_token: jwtToken,
            ...(approved ? { access } : { error: 'access_denied' }),
          }),
        });

        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          setError(body?.error_description || 'Authorization failed. Please try again.');
          setIsLoading(false);
          return;
        }

        const { redirect_url } = await resp.json();
        if (!redirect_url) {
          setError('Authorization failed: no redirect returned.');
          setIsLoading(false);
          return;
        }
        window.location.href = redirect_url;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setIsLoading(false);
      }
    },
    [state, internal_state, access],
  );

  const shell =
    'relative min-h-screen flex items-center justify-center overflow-hidden ' +
    'bg-slate-50 dark:bg-slate-950 p-4';

  // Soft brand-blue atmosphere behind the card — calm, not loud.
  const backdrop = (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(83,189,235,0.22), transparent 65%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-24 h-[28rem] w-[28rem] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(37,154,233,0.14), transparent 65%)' }}
      />
    </>
  );

  if (!client_id || !redirect_uri) {
    return (
      <div className={shell}>
        {backdrop}
        <div className="animate-in fade-in slide-in-from-bottom-3 duration-500 relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/50">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <h1 className="mb-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Invalid authorization request
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            This link is missing required parameters (client_id or redirect_uri). Please start the
            connection again from the app you were using.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={shell}>
      {backdrop}

      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-900">
        {/* brand hairline */}
        <div className="h-1 w-full bg-gradient-to-r from-[#53bdeb] to-[#259ae9]" />

        <div className="p-7 sm:p-8">
          {/* Header — a connection motif: the app ⟷ Blueticks */}
          <div className="animate-in fade-in duration-500 flex flex-col items-center text-center" style={reveal(0)}>
            <div className="mb-5 flex items-center gap-3">
              {/* Requesting app — neutral/brand-agnostic (Claude, ChatGPT, or any
                  MCP client); the actual name is shown dynamically below. */}
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                <Blocks className="h-5 w-5 text-slate-400 dark:text-slate-500" />
              </div>
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#53bdeb]/10">
                <Link2 className="h-3.5 w-3.5 text-[#259ae9]" />
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#53bdeb]/30 bg-[#53bdeb]/10">
                <img src="/img/blueticks-btn-logo.svg" alt="Blueticks" className="h-7 w-auto" />
              </div>
            </div>

            <h1 className="text-[1.35rem] font-semibold leading-tight text-slate-900 dark:text-slate-50">
              Connect to Blueticks
            </h1>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
              An application is requesting access to your WhatsApp on Blueticks.
            </p>

            {/* BE#282: show WHERE access will be sent — the redirect host is the
                only field that reveals a malicious client's callback. Rendered as
                plain text (React-escaped), never as trusted markup. */}
            {redirectValid ? (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-left dark:border-slate-700 dark:bg-slate-800/50">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                  <Globe className="h-3.5 w-3.5" />
                  Your access will be sent to
                </div>
                <p className="mt-1 break-all text-sm font-semibold text-slate-900 dark:text-slate-50">
                  {redirectHost}
                </p>
                <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                  Only authorize if you recognise this destination.
                </p>
              </div>
            ) : (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-3.5 py-3 text-left dark:border-red-900/60 dark:bg-red-950/40">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <p className="break-all text-xs text-red-700 dark:text-red-300">
                  This request has no recognisable callback destination
                  {redirect_uri ? <span className="font-mono"> ({redirect_uri})</span> : ''}. Do not authorize it.
                </p>
              </div>
            )}

            <span
              className={
                'mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ' +
                (canWrite
                  ? 'bg-[#53bdeb]/10 text-[#1c86c4] dark:text-[#7fd0f2]'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400')
              }
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              {canWrite ? 'Read & write access' : 'Read-only access'}
            </span>
          </div>

          {/* Capabilities */}
          <div className="mt-7 space-y-2.5">
            {visibleCapabilities.map(({ Icon, title, detail }, i) => (
              <div
                key={title}
                className="animate-in fade-in slide-in-from-bottom-2 duration-500 flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-3 dark:border-slate-800 dark:bg-slate-800/40"
                style={reveal(i + 1)}
              >
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#53bdeb]/12 text-[#259ae9]">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    {detail}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <MCPAccessLevelPicker
              value={access}
              fullAllowed={fullAllowed}
              disabled={isLoading}
              onChange={setAccess}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="animate-in fade-in duration-300 mt-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 dark:border-red-900/60 dark:bg-red-950/40">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="animate-in fade-in duration-500 mt-6 flex gap-3" style={reveal(visibleCapabilities.length + 1)}>
            <Button
              variant="outline"
              onClick={() => handleConsent(false)}
              disabled={isLoading}
              className="h-11 flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Deny
            </Button>
            <Button
              onClick={() => handleConsent(true)}
              disabled={isLoading}
              className="h-11 flex-1 bg-[#53bdeb] text-white shadow-md shadow-[#53bdeb]/25 transition-all hover:-translate-y-0.5 hover:bg-[#259ae9] hover:shadow-lg active:translate-y-0"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Authorize
                </>
              )}
            </Button>
          </div>

          {/* Footer note — BE#282: the real revocation path (there is no MCP
              revocation UI; do not promise one). */}
          <div className="mt-5 space-y-1 text-center text-xs text-slate-400 dark:text-slate-500">
            <p className="flex items-center justify-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Only connect apps you trust.
            </p>
            <p className="leading-relaxed">
              To disconnect later, remove this connector in the app&apos;s own settings. An
              issued access token can stay valid for up to ~1 hour — to cut access
              immediately, disconnect your WhatsApp number at{' '}
              <a
                href="/connections"
                className="underline hover:text-slate-600 dark:hover:text-slate-300"
              >
                Connections
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCPOAuthPage;
